import { Fragment, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { api, type ChangeKind, type ChangeRow, type ConfigChange } from "../lib/api";
import { stamp } from "../lib/format";

/**
 * Who changed what, in words, for administrators. Every change the app recorded is one
 * line - who, when, what - and the ones that made a revision open it: a plan change
 * opens that day's board with its history, a settings save shows exactly which fields
 * moved against the version before. The raw action codes stay one switch away for
 * anyone who needs the record as the server wrote it.
 */

const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-top";
const input = "rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]";
const small = "rounded-md border border-line px-2 py-0.5 text-[12px] font-semibold hover:border-ink-3";

export const CHANGE_KINDS: Array<{ id: ChangeKind; label: string }> = [
  { id: "plan", label: "Plans: moves, locks, counters, belts" },
  { id: "ops", label: "Tows, holds, corrections, closures, built flights" },
  { id: "settings", label: "Settings" },
  { id: "accounts", label: "Accounts and roles" },
  { id: "integrations", label: "Keys, webhooks, texting, data sources" },
  { id: "sign-in", label: "Sign-ins and authenticators" },
  { id: "guide", label: "Guide edits" },
  { id: "other", label: "Everything else" },
];

const KIND_SHORT: Record<ChangeKind, string> = {
  plan: "Plan",
  ops: "Ops",
  settings: "Settings",
  accounts: "Accounts",
  integrations: "Integrations",
  "sign-in": "Sign-in",
  guide: "Guide",
  other: "Other",
};

/** A value in a settings comparison, short enough for a table cell. */
export function valueText(v: unknown): string {
  if (v === null || v === undefined || v === "") return "empty";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (typeof v === "number" || typeof v === "string") return String(v);
  if (Array.isArray(v)) return v.length ? v.map(valueText).join(", ") : "none";
  const s = JSON.stringify(v);
  return s.length > 80 ? `${s.slice(0, 77)}...` : s;
}

export function SettingsChanges({ changes, truncated, previousId }: { changes: ConfigChange[]; truncated: boolean; previousId: number | null }) {
  if (!changes.length) return <p className="text-[12px] text-ink-3">{previousId === null ? "The first version saved; nothing to compare it with." : "Nothing differs from the version before."}</p>;
  return (
    <div className="text-[12px]">
      <table className="w-full">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>Setting</th>
            <th className={th}>Before</th>
            <th className={th}>After</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c, i) => (
            <tr key={`${c.path}-${i}`} className="border-b border-line/60">
              <td className={clsx(td, "font-semibold")}>{c.path}</td>
              <td className={clsx(td, "text-ink-2")}>{valueText(c.before)}</td>
              <td className={clsx(td, "text-ink")}>{valueText(c.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && <p className="mt-1 text-ink-3">Only the first changes are shown; this version rewrote a great deal.</p>}
    </div>
  );
}

/** Fetches and shows what one settings version changed against the one before it. */
export function SettingsDiffView({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["settings-diff", id], queryFn: () => api.settingsDiff(id) });
  if (q.isLoading) return <Loader2 className="size-4 animate-spin text-ink-3" />;
  if (!q.data) return <p className="text-[12px] text-bad">Could not read that version.</p>;
  return <SettingsChanges changes={q.data.changes} truncated={q.data.truncated} previousId={q.data.previousId} />;
}

/** The lines themselves, given the rows: kept apart from the fetching so it can be rendered on its own. */
export function ChangeList({ rows, tz, raw, onOpenDay, renderDiff }: { rows: ChangeRow[]; tz: string; raw?: boolean; onOpenDay?: (date: string) => void; renderDiff?: (id: number) => React.ReactNode }) {
  const [open, setOpen] = useState<number | null>(null);
  if (!rows.length) return <p className="mt-2 text-[13px] text-ink-3">Nothing recorded for this filter.</p>;
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>When</th>
          <th className={th}>Who</th>
          <th className={th}>What</th>
          <th className={th}></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.id}>
            <tr className="border-b border-line">
              <td className={clsx(td, "tabular whitespace-nowrap")}>{stamp(r.at, tz)}</td>
              <td className={clsx(td, "whitespace-nowrap")}>{r.user ?? <span className="text-ink-3">the app</span>}</td>
              <td className={td}>
                <span className="mr-1.5 rounded bg-panel-2 px-1 text-[10px] font-bold uppercase text-ink-3">{KIND_SHORT[r.kind]}</span>
                {r.text}
                {raw && (
                  <div className="mt-0.5 font-mono text-[11px] text-ink-3 break-all">
                    {r.action} {r.detail ? JSON.stringify(r.detail) : ""}
                  </div>
                )}
              </td>
              <td className={clsx(td, "whitespace-nowrap text-right")}>
                {r.board?.date && onOpenDay && (
                  <button className={small} onClick={() => onOpenDay(r.board!.date)} title={r.board.versionId ? `Board version ${r.board.versionId}` : "Open the board for that day"}>
                    Open board
                  </button>
                )}
                {r.settingsVersion !== undefined && (
                  <button
                    className={clsx(small, "inline-flex items-center gap-1")}
                    onClick={() => setOpen(open === r.id ? null : r.id)}
                    aria-expanded={open === r.id}
                  >
                    {open === r.id ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />} What changed
                  </button>
                )}
              </td>
            </tr>
            {open === r.id && r.settingsVersion !== undefined && (
              <tr className="border-b border-line bg-panel-2/40">
                <td colSpan={4} className="px-3 py-2">
                  {renderDiff ? renderDiff(r.settingsVersion) : <SettingsDiffView id={r.settingsVersion} />}
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

export function ChangesTable({ tz, today, onOpenDay }: { tz: string; today: string; onOpenDay?: (date: string) => void }) {
  const [kind, setKind] = useState<ChangeKind | "">("");
  const [user, setUser] = useState("");
  const [from, setFrom] = useState(() => DateTime.fromISO(today, { zone: tz }).minus({ days: 29 }).toISODate()!);
  const [to, setTo] = useState(today);
  const [raw, setRaw] = useState(false);
  const log = useQuery({
    queryKey: ["changes", kind, user, from, to],
    queryFn: () => api.changes({ kind: kind || undefined, user: user.trim() || undefined, from, to }),
  });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
        <select className={input} value={kind} onChange={(e) => setKind(e.target.value as ChangeKind | "")}>
          <option value="">Every kind of change</option>
          {CHANGE_KINDS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
        <input className={clsx(input, "w-44")} placeholder="Who (name or email)" value={user} onChange={(e) => setUser(e.target.value)} />
        <label className="flex items-center gap-1">
          From <input type="date" className={input} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex items-center gap-1">
          To <input type="date" className={input} value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="ml-auto flex items-center gap-1 text-ink-3">
          <input type="checkbox" className="size-3.5" checked={raw} onChange={(e) => setRaw(e.target.checked)} /> Show the raw record
        </label>
        {log.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
      </div>
      <p className="mb-2 text-[12px] text-ink-3">
        Every change made in the app, newest first. A plan change opens the board for that day, where History lists every version; a settings save shows the fields it changed against the version before.
      </p>
      {log.data && <ChangeList rows={log.data.rows} tz={tz} raw={raw} onOpenDay={onOpenDay} />}
    </>
  );
}
